import { SessionLeafNode } from '@biconomy/modules/dist/types/interfaces/ISessionStorage'
import { Period } from '@kwenta/sdk/constants'
import {
	FuturesMarketAsset,
	FuturesOrderType,
	PositionSide,
	SmartMarginOrderType,
	SwapDepositToken,
} from '@kwenta/sdk/types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AbstractionToken } from 'services/biconomy/accountAbstraction.types'

import { DEFAULT_PERPS_PROVIDER } from 'constants/futures'
import { HistoryTab } from 'sections/dashboard/History/HistoryTabs'
import { ZERO_STATE_TRADE_INPUTS } from 'state/constants'

import type { TableFilter } from '../../sections/futures/UserInfo/TableFilters/TableFilters'

import { EditPositionInputs, TradePanelInputs } from './common/types'
import {
	FuturesState,
	TradingModes,
	DelegationAccountInfo,
	UIPreferencesPayload,
	ManageModalType,
	TableFilteredFuturesTabs,
	DashboardHistoryTableFilter,
	PerpsProvider,
} from './types'

export const FUTURES_INITIAL_STATE: FuturesState = {
	selectedMarketAsset: {
		[PerpsProvider.SNX_V2]: FuturesMarketAsset.sETH,
		[PerpsProvider.SNX_V3]: FuturesMarketAsset.sETH,
		[PerpsProvider.PERENNIAL_V2]: FuturesMarketAsset.sETH,
	},
	tradePanelDrawerOpen: false,
	preferences: {
		profitCalculatorVisible: false,
		selectedManageModalType: ManageModalType.Bridge,
		selectedSwapDepositToken: SwapDepositToken.SUSD,
		selectedInputHours: 1,
		selectedChart: 'price',
		selectedInputDenomination: 'usd',
		selectedProvider: DEFAULT_PERPS_PROVIDER,
		showHistory: true,
		showPositionLines: true,
		showOrderLines: true,
		showRealizedPnL: true,
		showTestnets: process.env.NODE_ENV !== 'production' ? true : false,
		showFunding: true,
		tradingMode: TradingModes.DEFAULT,
		userInfoCollapsed: false,
		userInfoShowAllMarkets: true,
		historicalFundingRatePeriod: Period.TWO_WEEKS,
		favoriteMarkets: [],
		portfolioChartType: 'account',
		userInfoTableFilter: {
			trades: null,
			transaction_history: null,
			order_history: null,
		},
		dashboardHistoryTableFilter: {
			positions: null,
			trades: null,
			transaction_history: null,
			order_history: null,
			pools: null,
		},
	},
	oneClickTrading: {
		ethBalance: '0',
		usdcBalance: '0',
		usdcEngineAllowance: '0',
		accountDelegated: false,
		sessionInfo: undefined,
		isSessionActive: false,
		estTxCost: {
			value: '0',
			asset: AbstractionToken.ETH,
		},
	},
	dashboard: {
		selectedPortfolioTimeframe: Period.ONE_WEEK,
	},
	leaderboard: {
		selectedTrader: undefined,
		pageSize: 10,
	},
	delegation: {
		selectedDelegationAddress: '',
		selectedSubAccountAddress: '',
		newNickname: '',
		addressBook: {},
	},
	sltpModalInputs: {
		stopLossPrice: '',
		takeProfitPrice: '',
	},
	editPositionInputs: {
		nativeSizeDelta: '',
		marginDelta: '',
	},
	closePositionOrderInputs: {
		orderType: 'market',
		nativeSizeDelta: '',
		price: {
			value: '',
			invalidLabel: undefined,
		},
	},
	tradePanel: {
		isDraggingSlider: false,
		leverageSide: PositionSide.LONG,
		leverageInput: '',
		inputs: ZERO_STATE_TRADE_INPUTS,
	},
}

const futuresSlice = createSlice({
	name: 'futures',
	initialState: FUTURES_INITIAL_STATE,
	reducers: {
		setSelectedMarketAsset: (
			state,
			action: PayloadAction<{ asset: FuturesMarketAsset; provider: PerpsProvider }>
		) => {
			state.selectedMarketAsset[action.payload.provider] = action.payload.asset
		},
		setSelectedTrader: (
			state,
			action: PayloadAction<
				| {
						trader: string
						traderEns: string | undefined | null
						accountId: string | undefined
				  }
				| undefined
			>
		) => {
			state.leaderboard.selectedTrader = action.payload
		},
		setLeaderboardPageSize: (state, action: PayloadAction<number>) => {
			state.leaderboard.pageSize = action.payload
		},
		setSelectedPortfolioTimeframe: (state, action: PayloadAction<Period>) => {
			state.dashboard.selectedPortfolioTimeframe = action.payload
		},
		setTradePanelDrawerOpen: (state, action: PayloadAction<boolean>) => {
			state.tradePanelDrawerOpen = action.payload
		},
		setAbstractionEthBalance: (state, action: PayloadAction<string>) => {
			state.oneClickTrading.ethBalance = action.payload
		},
		setAbstractionTxCost: (
			state,
			action: PayloadAction<{ value: string; asset: AbstractionToken }>
		) => {
			state.oneClickTrading.estTxCost = action.payload
		},
		setAbstractionUsdcBalance: (state, action: PayloadAction<string>) => {
			state.oneClickTrading.usdcBalance = action.payload
		},
		setIsSessionActive: (state, action: PayloadAction<boolean>) => {
			state.oneClickTrading.isSessionActive = action.payload
		},
		setAbstractionUsdcEngineAllowance: (state, action: PayloadAction<string>) => {
			state.oneClickTrading.usdcEngineAllowance = action.payload
		},
		setAbstraction: (state, action: PayloadAction<string>) => {
			state.oneClickTrading.usdcBalance = action.payload
		},
		setDelegated: (state, action: PayloadAction<boolean>) => {
			state.oneClickTrading.accountDelegated = action.payload
		},
		setSession: (state, action: PayloadAction<SessionLeafNode | undefined>) => {
			state.oneClickTrading.sessionInfo = action.payload
		},
		setTradeStopLoss: (state, action: PayloadAction<string>) => {
			state.tradePanel.inputs.stopLossPrice = action.payload
		},
		setTradeTakeProfit: (state, action: PayloadAction<string>) => {
			state.tradePanel.inputs.takeProfitPrice = action.payload
		},
		setTradeInputs: (state, action: PayloadAction<Partial<TradePanelInputs<string>>>) => {
			state.tradePanel.inputs = {
				...(state.tradePanel.inputs ?? ZERO_STATE_TRADE_INPUTS),
				...action.payload,
			}
		},
		setMarginDelta: (state, action: PayloadAction<string>) => {
			state.tradePanel.inputs.marginDelta = action.payload
		},
		setLeverageInput: (state, action: PayloadAction<string>) => {
			state.tradePanel.leverageInput = action.payload
		},
		setLeverageSide: (state, action) => {
			state.tradePanel.leverageSide = action.payload
		},
		setTradePanelOrderPrice: (state, action: PayloadAction<string>) => {
			state.tradePanel.inputs.orderPrice.price = action.payload
		},
		setOrderPriceInvalidLabel: (state, action: PayloadAction<string | undefined>) => {
			state.tradePanel.inputs.orderPrice.invalidLabel = action.payload
		},
		setTradePanelOrderType: (state, action: PayloadAction<FuturesOrderType>) => {
			state.tradePanel.inputs.orderType = action.payload
		},
		setClosePositionOrderType: (state, action: PayloadAction<SmartMarginOrderType>) => {
			state.closePositionOrderInputs.orderType = action.payload
		},
		setClosePositionSizeDelta: (state, action: PayloadAction<string>) => {
			state.closePositionOrderInputs.nativeSizeDelta = action.payload
		},
		setClosePositionPrice: (
			state,
			action: PayloadAction<{ value: string; invalidLabel: string | null | undefined }>
		) => {
			state.closePositionOrderInputs.price = action.payload
		},
		setSelectedDelegationAddress: (state, action: PayloadAction<string>) => {
			state.delegation.selectedDelegationAddress = action.payload
		},
		setSelectedSubAccountAddress: (state, action: PayloadAction<string>) => {
			state.delegation.selectedSubAccountAddress = action.payload
		},
		setPreferences(state, action: PayloadAction<UIPreferencesPayload>) {
			state.preferences = {
				...state.preferences,
				...action.payload,
			}
		},
		setProfitCalculatorVisible(state, action: PayloadAction<boolean>) {
			state.preferences.profitCalculatorVisible = action.payload
		},
		addDelegateToAddressBook: (
			state,
			action: PayloadAction<{ wallet: string; delegate: DelegationAccountInfo }>
		) => {
			const { wallet, delegate } = action.payload
			if (state.delegation.addressBook[wallet]) {
				const addressExists = state.delegation.addressBook[wallet].some(
					({ address }) => address.toLowerCase() === delegate.address.toLowerCase()
				)
				if (!addressExists) {
					state.delegation.addressBook[wallet].push(delegate)
				}
			} else {
				state.delegation.addressBook[wallet] = [delegate]
			}
		},
		removeDelegateFromAddressBook: (
			state,
			action: PayloadAction<{ wallet: string; delegatedAddress: string }>
		) => {
			const { wallet, delegatedAddress } = action.payload
			if (state.delegation.addressBook[wallet]) {
				state.delegation.addressBook[wallet] = state.delegation.addressBook[wallet].filter(
					({ address }) => address.toLowerCase() !== delegatedAddress.toLowerCase()
				)
			}
		},
		renameAddress: (
			state,
			action: PayloadAction<{ wallet: string; delegate: DelegationAccountInfo }>
		) => {
			const { wallet, delegate } = action.payload
			if (state.delegation.addressBook[wallet]) {
				const existingDelegateIndex = state.delegation.addressBook[wallet].findIndex(
					({ address }) => address.toLowerCase() === delegate.address.toLowerCase()
				)

				if (existingDelegateIndex !== -1) {
					state.delegation.addressBook[wallet][existingDelegateIndex].nickname = delegate.nickname
				} else {
					state.delegation.addressBook[wallet].push(delegate)
				}
			} else {
				state.delegation.addressBook[wallet] = [delegate]
			}
		},
		setNewNickname: (state, action: PayloadAction<string>) => {
			state.delegation.newNickname = action.payload
		},
		setSLTPModalStopLoss: (state, action: PayloadAction<string>) => {
			state.sltpModalInputs.stopLossPrice = action.payload
		},
		setSLTPModalTakeProfit: (state, action: PayloadAction<string>) => {
			state.sltpModalInputs.takeProfitPrice = action.payload
		},
		setEditPositionInputs: (smartMargin, action: PayloadAction<EditPositionInputs<string>>) => {
			smartMargin.editPositionInputs = action.payload
		},
		toggleFavoriteMarket: (state, action: PayloadAction<{ asset: FuturesMarketAsset }>) => {
			const { asset } = action.payload
			if (state.preferences.favoriteMarkets) {
				const marketExists = state.preferences.favoriteMarkets.some(
					(market) => market.toLowerCase() === asset.toLowerCase()
				)
				if (marketExists) {
					state.preferences.favoriteMarkets = state.preferences.favoriteMarkets.filter(
						(market) => market.toLowerCase() !== asset.toLowerCase()
					)
				} else {
					state.preferences.favoriteMarkets.push(asset)
				}
			} else {
				state.preferences.favoriteMarkets = [asset]
			}
		},
		setUserInfoTableFilter: (
			state,
			action: PayloadAction<{
				tableFilter: TableFilter | null
				futuresTab: TableFilteredFuturesTabs
			}>
		) => {
			const { tableFilter, futuresTab } = action.payload
			state.preferences.userInfoTableFilter[futuresTab] = tableFilter
		},
		setIsDraggingSlider: (state, action: PayloadAction<boolean>) => {
			state.tradePanel.isDraggingSlider = action.payload
		},
		setDashboardHistoryTableFilter: (
			state,
			action: PayloadAction<{
				tableFilter: DashboardHistoryTableFilter | null
				historyTab: HistoryTab
			}>
		) => {
			const { tableFilter, historyTab } = action.payload
			state.preferences.dashboardHistoryTableFilter[historyTab] = tableFilter
		},
	},
})

export default futuresSlice.reducer

export const {
	setSelectedMarketAsset,
	setSelectedTrader,
	setLeaderboardPageSize,
	setSelectedPortfolioTimeframe,
	setTradePanelDrawerOpen,
	setAbstractionEthBalance,
	setAbstractionUsdcBalance,
	setAbstractionUsdcEngineAllowance,
	setDelegated,
	setSession,
	setIsSessionActive,
	setSelectedDelegationAddress,
	setSelectedSubAccountAddress,
	addDelegateToAddressBook,
	removeDelegateFromAddressBook,
	renameAddress,
	setNewNickname,
	setPreferences,
	setSLTPModalStopLoss,
	setSLTPModalTakeProfit,
	setEditPositionInputs,
	setProfitCalculatorVisible,
	toggleFavoriteMarket,
	setUserInfoTableFilter,
	setIsDraggingSlider,
	setDashboardHistoryTableFilter,
	setLeverageSide,
	setLeverageInput,
	setTradePanelOrderType,
	setTradeInputs,
	setTradePanelOrderPrice,
	setOrderPriceInvalidLabel,
	setTradeStopLoss,
	setTradeTakeProfit,
	setMarginDelta,
	setClosePositionOrderType,
	setClosePositionSizeDelta,
	setClosePositionPrice,
	setAbstractionTxCost,
} = futuresSlice.actions
